import { FC, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

import videoPlayHoverBtn from 'assets/images/video-play-button-hover.svg';
import videoPlayBtn from 'assets/images/video-play-button.svg';

import shadow from 'atoms/shadows/shadows';

import Image from 'quarks/Image';
import Text from 'quarks/Text';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentCardDeck, ReviewCardDeckBody, ReviewCardDeckVideo } from 'graphqlTypes';

const ReviewCardDeck: FC<ContentfulComponentCardDeck> = ({ card }) => {
  const [playingUrl, setPlayingUrl] = useState('');
  const [imageHover, setImageHover] = useState(false);

  const getBodyContent = (data: ReviewCardDeckBody) => (
    <div className="body">
      <div className="logo">
        <img src={data?.person?.company?.logo?.file?.url} alt="company-logo" />
      </div>
      <Text textStyle="textSm" textColor="gray-900">
        {richTextParser(data?.body)}
      </Text>
      <div className="person-footer">
        <Text textStyle="textMd" fontWeight="bold" textColor="gray-900" className="name">
          {data?.person?.name}
        </Text>
        <Text textStyle="textXs" textColor="gray-900">
          {data?.person?.position}, {data?.person?.company?.company}
        </Text>
      </div>
    </div>
  );

  const getFeaturedVideo = (data: ReviewCardDeckVideo) => {
    const isVideoPlaying = data?.youTubeUrl === playingUrl;

    return (
      <button
        className="card-1-video video"
        onMouseEnter={() => setImageHover(true)}
        onMouseLeave={() => setImageHover(false)}
        onClick={() => setPlayingUrl(data?.youTubeUrl)}
      >
        {isVideoPlaying ? (
          <ReactPlayer url={data?.youTubeUrl} playing={isVideoPlaying} width="100%" height="100%" controls={true} />
        ) : (
          <>
            <Image image={{ file: { url: data?.featuredImage?.imageDesktop?.file?.url } }} className="video-thumb" />
            <img src={imageHover ? videoPlayHoverBtn : videoPlayBtn} alt="video-play-btn" className="play-button" />
          </>
        )}
      </button>
    );
  };

  return (
    <Container>
      {card?.map((data, index) => {
        const isFeatured = data?.youTubeUrl;

        return (
          <div className={`${isFeatured ? 'featured-card' : 'card'}`} key={`card-${index}`}>
            {isFeatured ? (
              <>
                {getBodyContent(data)}
                {getFeaturedVideo(data)}
              </>
            ) : (
              <>{getBodyContent(data)}</>
            )}
          </div>
        );
      })}
    </Container>
  );
};

export default ReviewCardDeck;

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 30px;
  width: 100%;

  > div {
    flex-direction: row;
    :nth-child(odd of .featured-card) {
      flex-direction: row-reverse;
    }
  }

  .card {
    box-shadow: ${shadow.sm};
    flex: 1 1 calc(33.333% - 30px);
    padding: 30px 30px 130px;
    position: relative;
    @media screen and (max-width: 575px) {
      padding: 30px;
    }
  }

  button {
    border: none;
    padding: 0;
  }

  .featured-card {
    box-shadow: ${shadow.sm};
    display: flex;
    height: 450px;
    flex-basis: 100%;
    .video {
      flex: 1 1 0%;
      position: relative;

      .video-thumb {
        object-fit: cover;
        width: 100%;
        height: 100%;
      }

      .play-button {
        position: absolute;
        inset: 0;
        margin: auto;
      }
    }

    .body {
      flex: 0 1 calc(33.333% - 20px);
      position: relative;
      padding: 30px;
    }
  }

  .logo {
    display: flex;
    margin-bottom: 48px;

    img {
      object-fit: contain;
      max-width: 137px;
      max-height: 72px;
    }
  }

  .person-footer {
    position: absolute;
    bottom: 32px;
    display: flex;
    flex-direction: column;

    .name {
      margin-bottom: 10px;
    }
  }

  @media (max-width: 797px) {
    .featured-card {
      .body,
      .video {
        width: 50%;
        flex: none;
      }
    }
  }

  @media (max-width: 575px) {
    .featured-card {
      flex-direction: column-reverse;
      height: auto;

      .body,
      .video {
        width: 100%;
      }

      .body {
        height: max-content;
      }

      .video {
        height: 196px;
        flex: none !important;
      }

      &.left-video {
        flex-direction: column-reverse;
      }
    }

    > div {
      :nth-child(odd of .featured-card) {
        flex-direction: column-reverse;
      }
    }

    .person-footer {
      position: relative;
      bottom: inherit;
      margin-top: 30px;
    }
  }
`;
